import React, { useState, useCallback, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { BarChart3, List, Download, Loader2 } from 'lucide-react';
import Pagination from '@/components/ui/PaginationWrapper';
/**
 * ChartWidget - A reusable widget component for dashboard charts
 * 
 * Features:
 * - Title and subtitle with legend value
 * - View toggle between Chart and List views
 * - Export functionality
 * - Loading and error states
 * - Domain-aware colors via CSS variables
 * - Pagination for list view
 */
export function ChartWidget({
    title,
    subtitle,
    legendValue,
    legendLabel,
    isLoading = false,
    error = null,
    noDataMessage = 'No data available',
    className,
    // Chart view props
    chartContent,
    chartType = 'Bar', // 'Bar', 'Pie', 'Line', etc. - used for tooltip
    // List view props
    listData = [],
    listHeaders = [],
    onListItemClick,
    // Export
    onExport,
    exportTitle,
    // Additional controls
    additionalControls,
    // Hide toggle if only one view needed
    showToggle = true,
    defaultView = 'chart',
    // Full height for container
    minHeight = '350px',
}) {
    const [viewMode, setViewMode] = useState(defaultView);
    const [activePage, setActivePage] = useState(1);
    const [isFlipping, setIsFlipping] = useState(false);
    const listScrollRef = useRef(null);
    const listScrollEdgesRef = useRef({ scrollable: false, atTop: true, atBottom: true });
    const [isListScrollable, setIsListScrollable] = useState(false);
    const itemsPerPage = 6;

    // Handle view toggle with flip animation
    const toggleView = useCallback((mode) => {
        if (mode === viewMode || isFlipping) return; // Prevent multiple clicks during animation
        
        setIsFlipping(true);
        setViewMode(mode);
        setActivePage(1); // Reset pagination when switching views
        
        // Wait for animation to complete before allowing another toggle
        setTimeout(() => {
            setIsFlipping(false);
        }, 600); // Match the flip animation duration
    }, [viewMode, isFlipping]);

    // Calculate pagination
    const totalItems = listData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const paginatedData = listData.slice(
        (activePage - 1) * itemsPerPage,
        activePage * itemsPerPage
    );

    // After a filter/refetch, list length can shrink so the current page is past the last
    // page — slice is empty even though listData has rows. Toggling chart/list reset page
    // to 1; clamp here so the table updates immediately.
    useEffect(() => {
        if (listData.length === 0) {
            setActivePage(1);
            return;
        }
        const totalPagesForData = Math.ceil(listData.length / itemsPerPage);
        setActivePage((p) => (p > totalPagesForData ? 1 : p));
    }, [listData, listData.length, itemsPerPage]);

    // Handle page change
    const handlePageChange = useCallback((pageNumber) => {
        setActivePage(pageNumber);
    }, []);

    // Check if we have data
    const hasChartData = !!chartContent;
    const hasListData = listData.length > 0;
    const hasData = hasChartData || hasListData;

    // Forward page scroll only when the list table hits its scroll boundary (preserve-3d blocks chaining)
    useEffect(() => {
        const el = listScrollRef.current;
        if (!el || viewMode !== 'list') return;

        const updateEdges = () => {
            const scrollable = el.scrollHeight > el.clientHeight + 1;
            listScrollEdgesRef.current = {
                scrollable,
                atTop: el.scrollTop <= 1,
                atBottom: el.scrollTop + el.clientHeight >= el.scrollHeight - 1,
            };
            setIsListScrollable((prev) => (prev === scrollable ? prev : scrollable));
        };

        updateEdges();
        el.addEventListener('scroll', updateEdges, { passive: true });
        const resizeObserver = new ResizeObserver(updateEdges);
        resizeObserver.observe(el);

        const handleWheel = (e) => {
            if (e.ctrlKey) return;

            const { scrollable, atTop, atBottom } = listScrollEdgesRef.current;
            const scrollingUp = e.deltaY < 0;
            const scrollingDown = e.deltaY > 0;

            if (!scrollable || (scrollingUp && atTop) || (scrollingDown && atBottom)) {
                e.preventDefault();
                const scrollRoot = document.scrollingElement || document.documentElement;
                scrollRoot.scrollTop += e.deltaY;
                scrollRoot.scrollLeft += e.deltaX;
            }
        };

        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            el.removeEventListener('scroll', updateEdges);
            el.removeEventListener('wheel', handleWheel);
            resizeObserver.disconnect();
        };
    }, [viewMode, paginatedData, activePage, listData.length]);

    return (
        <Card className={cn("h-full flex flex-col", className)}>
            {/* Header with title, legend, and controls */}
            <CardHeader className="pb-3 flex-shrink-0">
                <div className="flex items-start justify-between gap-4">
                    {/* Title and Legend */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                            {legendValue && typeof legendValue !== 'object' && (
                                <span className="text-lg font-bold text-primary">
                                    {legendValue}
                                </span>
                            )}
                            <CardTitle className="text-lg font-semibold text-foreground truncate" title={title}>
                                {title}
                            </CardTitle>
                        </div>
                        {subtitle && (
                            <CardDescription className="text-sm text-muted-foreground mt-1">
                                {subtitle}
                            </CardDescription>
                        )}
                        {legendLabel && (
                            <p className="text-xs text-muted-foreground mt-1">
                                {legendLabel}
                            </p>
                        )}
                        {legendValue && typeof legendValue === 'object' && (
                            <>
                                {console.log('ChartWidget - Rendering object legendValue:', legendValue)}
                                <div className="mt-4">
                                    {legendValue}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Controls */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                        {additionalControls}
                        
                        {/* Export button */}
                        {onExport && (
                            <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onExport}
                                            className="h-8 w-8 cursor-pointer"
                            >
                                <Download className="h-4 w-4" />
                            </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Download Report</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}

                        {/* View toggle */}
                        {showToggle && (
                            <div className="flex items-center border rounded-md p-0.5 bg-muted/50">
                                <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                <Button
                                                variant={viewMode === 'chart' ? 'default' : 'ghost'}
                                    size="icon"
                                    onClick={() => toggleView('chart')}
                                                className={cn(
                                                    "h-7 w-7 cursor-pointer",
                                                    viewMode === 'chart' && "bg-primary text-primary-foreground hover:bg-primary/90"
                                                )}
                                >
                                    <BarChart3 className="h-4 w-4" />
                                </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>{chartType}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                <Button
                                                variant={viewMode === 'list' ? 'default' : 'ghost'}
                                    size="icon"
                                    onClick={() => toggleView('list')}
                                                className={cn(
                                                    "h-7 w-7 cursor-pointer",
                                                    viewMode === 'list' && "bg-primary text-primary-foreground hover:bg-primary/90"
                                                )}
                                >
                                    <List className="h-4 w-4" />
                                </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>List</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                        )}
                    </div>
                </div>
            </CardHeader>

            {/* Content */}
            <CardContent className={cn(
                "flex-1 min-h-0 pt-0 pb-4",
                viewMode === 'chart' && !isFlipping ? "overflow-visible" : "overflow-hidden"
            )}>
                <div 
                    className="relative h-full min-h-0 chart-content-wrapper"
                    style={{ minHeight }}
                >
                    {/* Loading State */}
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-10 rounded-lg">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    )}

                    {/* Error State */}
                    {error && !isLoading && (
                        <div className="flex items-center justify-center h-full text-destructive">
                            <p className="text-sm">{error}</p>
                        </div>
                    )}

                    {/* No Data State */}
                    {!isLoading && !error && !hasData && (
                        <div className="flex items-center justify-center h-full text-muted-foreground">
                            <p className="text-sm">{noDataMessage}</p>
                        </div>
                    )}

                    {/* Flip Container */}
                    {!isLoading && !error && hasData && (
                        <div 
                            className={cn(
                                "flip-container h-full min-h-0 w-full relative",
                                viewMode === 'list' && 'is-flipped',
                                isFlipping && 'is-flipping'
                            )}
                        >
                            {/* Chart View (Front) */}
                            {hasChartData && (
                                <div 
                                    className={cn(
                                        "flip-card-face flip-card-front absolute inset-0",
                                        viewMode === 'list' && "pointer-events-none"
                                    )}
                                    aria-hidden={viewMode === 'list'}
                                >
                            {chartContent}
                        </div>
                    )}

                            {/* List View (Back) */}
                            {hasListData && (
                                <div 
                                    className={cn(
                                        "flip-card-face flip-card-back absolute inset-0 flex flex-col min-h-0",
                                        viewMode === 'chart' && "pointer-events-none"
                                    )}
                                    aria-hidden={viewMode === 'chart'}
                                >
                                    <div className="flex flex-col flex-1 min-h-0">
                                        {/* Scrollable table */}
                                        <div
                                            ref={listScrollRef}
                                            className={cn(
                                                "chart-list-scroll-area flex-1 min-h-0 overscroll-auto [-webkit-overflow-scrolling:touch]",
                                                isListScrollable ? "overflow-auto" : "overflow-visible"
                                            )}
                                        >
                                            <div className="min-w-max w-full">
                                                {/* Table Header - Sticky */}
                                                {listHeaders.length > 0 && (
                                                    <div 
                                                        className="sticky top-0 z-10 grid gap-2 p-3 font-medium text-sm bg-card border-b border-border text-primary"
                                                        style={{ gridTemplateColumns: `repeat(${listHeaders.length}, minmax(100px, 1fr))` }}
                                                    >
                                                        {listHeaders.map((header, idx) => {
                                                            const isNumericColumn = header.includes('# of') || 
                                                                                   header === 'Transactions';
                                                            return (
                                                                <div 
                                                                    key={`header-${idx}`} 
                                                                    className={cn("whitespace-nowrap pr-2", isNumericColumn && "text-center")}
                                                                >
                                                                    {header}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                {/* Table Body */}
                                                <div className="divide-y divide-border">
                                                    {paginatedData.map((row, rowIdx) => {
                                                        const rowKey = (activePage - 1) * itemsPerPage + rowIdx;
                                                        const isTotal = row.some?.(cell => 
                                                            typeof cell === 'string' && cell.toLowerCase() === 'total'
                                                        );
                                                        return (
                                                            <div
                                                                key={`row-${rowKey}`}
                                                                className={cn(
                                                                    "grid gap-2 p-3 text-sm",
                                                                    isTotal && "font-semibold bg-muted/30"
                                                                )}
                                                                style={{ gridTemplateColumns: `repeat(${row.length || listHeaders.length}, minmax(100px, 1fr))` }}
                                                            >
                                                                {row.map?.((cell, cellIdx) => {
                                                                    const nonClickableHeaders = [
                                                                        'Prescriber Id', 
                                                                        'Prescriber Phone',
                                                                        'Prescriber Address',
                                                                        '# of  Transactions', 
                                                                        '# of Transactions',
                                                                        'Address',
                                                                        'Patient Savings',
                                                                        '# of paid claims',
                                                                        'Paid Claims (%)',
                                                                        '# of Paid Claims'
                                                                    ];
                                                                    const headerName = listHeaders[cellIdx];
                                                                    const isClickable = onListItemClick && !isTotal && !nonClickableHeaders.includes(headerName);
                                                                    
                                                                    const isNumericColumn = headerName?.includes('# of') || 
                                                                                           headerName === 'Transactions';
                                                                    
                                                                    return (
                                                                        <div 
                                                                            key={`cell-${cellIdx}`} 
                                                                            className={cn(
                                                                                "whitespace-nowrap pr-2",
                                                                                isNumericColumn && "text-center",
                                                                                isClickable && "cursor-pointer hover:text-primary transition-colors"
                                                                            )}
                                                                            title={typeof cell === 'string' ? cell : undefined}
                                                                            onClick={isClickable ? () => onListItemClick?.(row, rowIdx, cellIdx) : undefined}
                                                                        >
                                                                            {cell}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Pagination — fixed below scroll area */}
                                        {totalPages > 1 && (
                                            <div className="mt-3 flex-shrink-0 pt-2 border-t border-border bg-card">
                                                <Pagination
                                                    activePage={activePage}
                                                    itemsCountPerPage={itemsPerPage}
                                                    totalItemsCount={totalItems}
                                                    pageRangeDisplayed={5}
                                                    onChange={handlePageChange}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

/**
 * Helper hook to get theme-aware chart colors
 */
export function useChartColors() {
    const getColors = useCallback(() => {
        if (typeof window === 'undefined') {
            return {
                primary: '#44b672',
                accent: '#f19f57',
                secondary: '#2a524b',
                muted: '#94a3b8',
            };
        }
        
        const styles = getComputedStyle(document.documentElement);
        return {
            primary: styles.getPropertyValue('--primary').trim() || '#44b672',
            accent: styles.getPropertyValue('--accent').trim() || '#f19f57',
            secondary: styles.getPropertyValue('--theme-primary').trim() || '#2a524b',
            muted: styles.getPropertyValue('--muted-foreground').trim() || '#94a3b8',
            chart1: styles.getPropertyValue('--chart-1').trim(),
            chart2: styles.getPropertyValue('--chart-2').trim(),
            chart3: styles.getPropertyValue('--chart-3').trim(),
            chart4: styles.getPropertyValue('--chart-4').trim(),
            chart5: styles.getPropertyValue('--chart-5').trim(),
        };
    }, []);

    return getColors;
}

export default ChartWidget;
